/**
 * useBuilderAgentTools — wires the campaign builder into FutureBot.
 *
 * Produces the useAgentPage() registration for this page: live tool
 * implementations bound to the useCampaignBuilder instance (reading fresh
 * state through a ref) plus a getContext() snapshot with reference data
 * (stage slugs, tag values, existing campaigns) for the model's prompt.
 *
 * Also owns the "watch it build" affordances:
 *   - agentTouchedIds: node ids FutureBot just changed (NodeCard glows purple)
 *   - agentBuildStamp: set when build_campaign replaces the canvas so
 *     NodeCards mount with a staggered entrance animation.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { useCrmSettings } from '../../../../contexts/CrmSettingsContext';
import { useImpersonation } from '../../../../contexts/ImpersonationContext';
import { fetchTemplates } from '../../../../api/supabase/automations';

const GLOW_DURATION_MS = 2600;

const BUILDER_SUGGESTIONS = [
  'Build a new-lead follow-up campaign: text right away, wait 2 days, then email',
  'What kinds of steps and triggers can campaigns have?',
  'Summarize what this campaign does',
];

export function useBuilderAgentTools(builder, { enabled = true, onSaved } = {}) {
  const { dealStages, tags } = useCrmSettings();
  const { effectiveUserId } = useImpersonation();

  const [agentTouchedIds, setAgentTouchedIds] = useState(() => new Set());
  const [agentBuildStamp, setAgentBuildStamp] = useState(0);

  // Fresh-state refs so the (stable) tool closures never read stale data.
  const builderRef = useRef(builder);
  builderRef.current = builder;
  const settingsRef = useRef({ dealStages, tags });
  settingsRef.current = { dealStages, tags };
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;
  const campaignsCacheRef = useRef(null);
  const effectiveUserIdRef = useRef(effectiveUserId);
  effectiveUserIdRef.current = effectiveUserId;

  const glowNodes = useCallback((ids) => {
    const idList = Array.isArray(ids) ? ids : [ids];
    setAgentTouchedIds((prev) => {
      const next = new Set(prev);
      idList.forEach((id) => id && next.add(id));
      return next;
    });
    setTimeout(() => {
      setAgentTouchedIds((prev) => {
        const next = new Set(prev);
        idList.forEach((id) => next.delete(id));
        return next;
      });
    }, GLOW_DURATION_MS);
  }, []);

  const findNode = useCallback((nodeId) => {
    return builderRef.current.nodes.find((n) => n.id === nodeId) || null;
  }, []);

  // -------------------------------------------------------------------------
  // Tool implementations (names must match functions/FutureBot/toolsets.js)
  // -------------------------------------------------------------------------

  const tools = useMemo(() => ({
    get_campaign_state: () => {
      const { template, nodes, isDirty } = builderRef.current;
      return {
        template: {
          name: template?.name || '',
          description: template?.description || '',
          triggerType: template?.triggerType || 'MANUAL_ADD',
          triggerConfig: template?.triggerConfig || {},
          enrollmentRules: template?.enrollmentRules || {},
          isSaved: !!template?.id,
          hasUnsavedChanges: !!isDirty,
        },
        nodeCount: nodes.length,
        nodes: nodes.map((n, i) => ({
          index: i,
          position: i + 1,
          id: n.id,
          subType: n.subType,
          config: n.config,
          nextNodeId: n.nextNodeId,
          falseNodeId: n.falseNodeId,
        })),
      };
    },

    build_campaign: (args) => {
      try {
        const result = builderRef.current.importCampaign(args);
        setAgentBuildStamp(Date.now());
        return { ok: true, nodeCount: result.nodeCount };
      } catch (err) {
        return { error: err.message };
      }
    },

    add_node: (args) => {
      const { subType, config, afterIndex, branch, parentNodeId } = args || {};
      if (branch === 'false' && parentNodeId && !findNode(parentNodeId)) {
        return { error: `parentNodeId "${parentNodeId}" does not exist. Call get_campaign_state for current ids.` };
      }
      const newId = builderRef.current.addNode(
        subType,
        Number.isInteger(afterIndex) ? afterIndex : null,
        { branch: branch === 'false' ? 'false' : 'true', parentIfElseId: parentNodeId || null }
      );
      if (config && typeof config === 'object') {
        builderRef.current.updateNodeConfig(newId, config);
      }
      glowNodes(newId);
      return { ok: true, nodeId: newId };
    },

    update_node_config: (args) => {
      const { nodeId, config } = args || {};
      if (!findNode(nodeId)) {
        return { error: `Node "${nodeId}" does not exist. Call get_campaign_state for current ids.` };
      }
      builderRef.current.updateNodeConfig(nodeId, config || {});
      glowNodes(nodeId);
      return { ok: true };
    },

    change_node_type: (args) => {
      const { nodeId, subType, config } = args || {};
      if (!findNode(nodeId)) {
        return { error: `Node "${nodeId}" does not exist. Call get_campaign_state for current ids.` };
      }
      builderRef.current.updateNodeSubType(nodeId, subType);
      if (config && typeof config === 'object') {
        builderRef.current.updateNodeConfig(nodeId, config);
      }
      glowNodes(nodeId);
      return { ok: true };
    },

    remove_node: (args) => {
      const { nodeId } = args || {};
      if (!findNode(nodeId)) {
        return { error: `Node "${nodeId}" does not exist. Call get_campaign_state for current ids.` };
      }
      builderRef.current.removeNode(nodeId);
      return { ok: true };
    },

    move_node: (args) => {
      const { fromIndex, toIndex } = args || {};
      const count = builderRef.current.nodes.length;
      if (!Number.isInteger(fromIndex) || !Number.isInteger(toIndex) ||
          fromIndex < 0 || fromIndex >= count || toIndex < 0 || toIndex >= count) {
        return { error: `Indexes out of range (0-${count - 1}). Call get_campaign_state for current positions.` };
      }
      builderRef.current.moveNode(fromIndex, toIndex);
      const moved = builderRef.current.nodes[fromIndex];
      if (moved) glowNodes(moved.id);
      return { ok: true };
    },

    set_false_branch: (args) => {
      const { nodeId, falseNodeId } = args || {};
      const node = findNode(nodeId);
      if (!node) {
        return { error: `Node "${nodeId}" does not exist. Call get_campaign_state for current ids.` };
      }
      if (!['IF_ELSE', 'AB_SPLIT', 'WAIT_FOR'].includes(node.subType)) {
        return { error: `Node "${nodeId}" is ${node.subType}; only IF_ELSE, AB_SPLIT, and WAIT_FOR have a false branch.` };
      }
      const target = falseNodeId && falseNodeId.trim() ? falseNodeId.trim() : null;
      if (target && !findNode(target)) {
        return { error: `falseNodeId "${target}" does not exist.` };
      }
      builderRef.current.setFalseNodeId(nodeId, target);
      glowNodes(nodeId);
      return { ok: true };
    },

    set_trigger: (args) => {
      const { triggerType, triggerConfig } = args || {};
      builderRef.current.setTemplateMeta({ triggerType, triggerConfig: triggerConfig || {} });
      return { ok: true };
    },

    set_campaign_meta: (args) => {
      const { name, description, allowReEnrollment } = args || {};
      const updates = {};
      if (typeof name === 'string' && name.trim()) updates.name = name.trim();
      if (typeof description === 'string') updates.description = description;
      if (typeof allowReEnrollment === 'boolean') {
        updates.enrollmentRules = {
          ...(builderRef.current.template?.enrollmentRules || {}),
          allow_re_enrollment: allowReEnrollment,
        };
      }
      if (Object.keys(updates).length === 0) {
        return { error: 'Nothing to update — pass name, description, and/or allowReEnrollment.' };
      }
      builderRef.current.setTemplateMeta(updates);
      return { ok: true };
    },

    save_campaign: async () => {
      const { template, nodes } = builderRef.current;
      if (!template?.name?.trim()) {
        return { error: 'The campaign needs a name before saving. Use set_campaign_meta first.' };
      }
      if (nodes.length === 0) {
        return { error: 'The campaign has no steps to save.' };
      }
      try {
        const saved = await builderRef.current.save();
        if (onSavedRef.current) onSavedRef.current(saved);
        return { ok: true, templateId: saved?.id || null, isNewCampaign: !!saved?._isNew };
      } catch (err) {
        return { error: `Save failed: ${err.message}` };
      }
    },
  }), [findNode, glowNodes]);

  // -------------------------------------------------------------------------
  // Page context for the system prompt
  // -------------------------------------------------------------------------

  const getContext = useCallback(async () => {
    const { template, nodes, isDirty } = builderRef.current;
    const { dealStages: stages, tags: tagList } = settingsRef.current;

    if (campaignsCacheRef.current === null) {
      try {
        // Ownership-scoped: company templates + the effective user's personal
        // ones, so the model never sees other users' personal campaign names.
        const templates = await fetchTemplates({
          activeOnly: true,
          forUserId: effectiveUserIdRef.current,
        });
        campaignsCacheRef.current = (templates || []).map((t) => ({ id: t.id, name: t.name }));
      } catch (err) {
        console.warn('[FutureBot] Could not load campaign list:', err.message);
        campaignsCacheRef.current = [];
      }
    }

    const draftLabel = template?.id
      ? `Editing saved campaign "${template?.name || 'Untitled'}"`
      : `New unsaved draft${template?.name ? ` "${template.name}"` : ''}`;

    return {
      referenceData: {
        stages: (stages || []).map((s) => ({ value: s.value, label: s.label })),
        tags: (tagList || []).map((t) => t.value),
        campaigns: campaignsCacheRef.current,
      },
      stateSummary: `${draftLabel} — ${nodes.length} step(s), trigger ${template?.triggerType || 'MANUAL_ADD'}${isDirty ? ', unsaved changes' : ''}.`,
    };
  }, []);

  const registration = useMemo(() => {
    if (!enabled) return null;
    return {
      pageId: 'crm-campaign-builder',
      title: 'Campaign Builder',
      tools,
      getContext,
      suggestions: BUILDER_SUGGESTIONS,
    };
  }, [enabled, tools, getContext]);

  return { registration, agentTouchedIds, agentBuildStamp };
}

export default useBuilderAgentTools;
